import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { checkLocationStatus, setUserLocation, setLocationFromAddress } from '../services/locationService'
import { useAuth } from '../hooks/useAuth'

const LocationContext = createContext()

const LOCATION_KEY = ['user-location']

const PROMPT_DISMISSED_KEY = 'location_prompt_dismissed'

// Every cache key whose result depends on the user's active delivery
// location. When the user moves (or switches via the mismatch prompt /
// location chip), we invalidate all of these so the rest of the app
// refetches in real time — no stale "near old address" results.
const LOCATION_DEPENDENT_KEYS = [['vendors'], ['featured-products'], ['grocery-home'], ['product-categories'], ['category-products'], ['trending'], ['cart'], ['delivery-fee'], ['promotions-home'], ['flash-sales'], ['banners']]

const invalidateLocationDependent = qc => {
  LOCATION_DEPENDENT_KEYS.forEach(key => qc.invalidateQueries({ queryKey: key }))
}

export const LocationProvider = ({ children }) => {
  const queryClient = useQueryClient()
  const [showPrompt, setShowPrompt] = useState(false)

  // The server keeps a location only for signed-in customers (anonymous
  // callers always get has_location=false), so a signed-out visitor — the
  // landing page — makes no request at all. Signing in or out refetches.
  const { token } = useAuth() || {}
  const signedIn = !!token
  const prevSignedIn = useRef(signedIn)
  useEffect(() => {
    if (prevSignedIn.current !== signedIn) {
      prevSignedIn.current = signedIn
      queryClient.invalidateQueries({ queryKey: LOCATION_KEY })
    }
  }, [signedIn, queryClient])

  const { data, isLoading: loading } = useQuery({
    queryKey: LOCATION_KEY,
    queryFn: async () => {
      if (!localStorage.getItem('token')) return null
      const data = await checkLocationStatus()
      if (data.has_location && data.location) {
        setShowPrompt(false)
        return data.location
      }
      // Note: the legacy in-app modal has been removed. When the user has no
      // location, callers (banners / chips) navigate to /account?tab=addresses
      // instead of opening a modal here.
      return null
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 1
  })

  const location = data || null
  const hasLocation = !!location

  const updateLocation = useCallback(
    async locData => {
      const payload = {
        latitude: locData.latitude,
        longitude: locData.longitude,
        address: locData.address || '',
        city: locData.city || '',
        area_name: locData.area_name || '',
        place_id: locData.place_id || '',
        source: locData.source || 'gps'
      }

      // Optimistic update for instant header feedback
      queryClient.setQueryData(LOCATION_KEY, payload)
      setShowPrompt(false)
      localStorage.setItem(PROMPT_DISMISSED_KEY, '1')

      try {
        const saved = await setUserLocation(payload)
        // API may return either the raw location or an envelope { location, has_location }
        const normalized = saved?.location || saved
        queryClient.setQueryData(LOCATION_KEY, normalized)
        // Refetch from server so a subsequent refresh shows the truly persisted value
        queryClient.invalidateQueries({ queryKey: LOCATION_KEY })
        // Fan-out only AFTER the server has persisted the new active
        // location — the dependent endpoints (e.g. /auth/vendors) read
        // the server-side active location, so refetching earlier would
        // return stale "old location" results.
        invalidateLocationDependent(queryClient)
        return normalized
      } catch (err) {
        console.warn('Failed to persist location to server:', err)
        // Server persist failed — still fan out so screens reflect the
        // optimistic local change rather than getting stuck on stale data.
        invalidateLocationDependent(queryClient)
        return payload
      }
    },
    [queryClient]
  )

  const useAddressAsLocation = useCallback(
    async addressId => {
      const saved = await setLocationFromAddress(addressId)
      const normalized = saved?.location || saved
      queryClient.setQueryData(LOCATION_KEY, normalized)
      queryClient.invalidateQueries({ queryKey: LOCATION_KEY })
      invalidateLocationDependent(queryClient)
      setShowPrompt(false)
      return normalized
    },
    [queryClient]
  )

  const clearLocation = useCallback(() => {
    queryClient.setQueryData(LOCATION_KEY, null)
    invalidateLocationDependent(queryClient)
  }, [queryClient])

  const dismissPrompt = useCallback(() => {
    setShowPrompt(false)
    localStorage.setItem(PROMPT_DISMISSED_KEY, '1')
  }, [])

  return (
    <LocationContext.Provider
      value={{
        location,
        hasLocation,
        loading,
        showPrompt,
        updateLocation,
        useAddressAsLocation,
        clearLocation,
        dismissPrompt,
        setShowPrompt
      }}
    >
      {children}
    </LocationContext.Provider>
  )
}

export const useLocation = () => {
  const ctx = useContext(LocationContext)
  if (!ctx) throw new Error('useLocation must be used within LocationProvider')
  return ctx
}

export default LocationContext
